import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { ApiCustomer } from "src/api/api_customer";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import WS from "src/api/ws";
import TablePagination from "src/components/table-pagination";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";

/**
 * The properties for {@link UserOverview}
 */
export type UserOverviewProps = {};

/** The limit of pagination */
const LIMIT = 15;

/**
 * Overview over all user accounts
 */
function UserOverview(props: UserOverviewProps) {
    const [t] = useTranslation("customer-user-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { page, search } = Route.useSearch();
    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.UsersChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <HeadingLayout heading={t("heading.user-overview")}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/c/users",
                                        search: {
                                            page: page,
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{tg("label.username")}</TableHeader>
                        <TableHeader>{t("label.external-id")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((user) => (
                        <TableRow key={user.uuid} href={"/c/users/$userId/general"} params={{ userId: user.uuid }}>
                            <TableCell>{user.display_name}</TableCell>
                            <TableCell>{user.external_id}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/c/users"}
                getSearchParams={(newPage: number) => ({ page: newPage, search: search })}
            />
        </HeadingLayout>
    );
}

/** The search parameter of the users view */
type UserOverviewSearch = {
    /** The page the user is currently on */
    page: number;
    /** The search term that should be used */
    search: string;
};

export const Route = createFileRoute("/_customer/c/users/")({
    component: UserOverview,
    validateSearch: (search: Record<string, unknown>): UserOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
        };
    },
    loaderDeps: ({ search: { page, search } }) => ({ page, search }),
    loader: async ({ deps: { page, search } }) =>
        await ApiCustomer.users.getAll({
            limit: LIMIT,
            offset: LIMIT * (page - 1),
            filter_name: search,
        }),
});
